import type { AxiosError } from "axios"
import type { WooCommerceProduct } from "@/services/woo-commerce-service"

export const parseTotalHeader = (value: unknown): number => {
  if (Array.isArray(value)) {
    return Number.parseInt(value[0] ?? "0", 10) || 0
  }

  if (typeof value === "string") {
    return Number.parseInt(value, 10) || 0
  }

  return 0
}

export const normalizeFilterToken = (value: string): string =>
  value.trim().toLowerCase().replace(/\s+/g, "-")

export const toPositiveNumberOrUndefined = (
  value: number | undefined
): number | undefined => {
  if (!(value && Number.isFinite(value) && value > 0)) {
    return undefined
  }

  return value
}

const toNumberOrZero = (value: string | undefined): number => {
  if (!value) {
    return 0
  }

  const parsedValue = Number.parseFloat(value)
  return Number.isFinite(parsedValue) ? parsedValue : 0
}

export const getProductPrice = (product: WooCommerceProduct): number => {
  const preferredPrice = toNumberOrZero(product.price)
  if (preferredPrice > 0) {
    return preferredPrice
  }

  return toNumberOrZero(product.regular_price)
}

export const getProductRating = (product: WooCommerceProduct): number =>
  Number.parseFloat(product.average_rating) || 0

export const getProductStoreTokens = (
  product: WooCommerceProduct
): Set<string> => {
  const store = product.store
  const tokens = new Set<string>()

  if (!store) {
    return tokens
  }

  if (store.id) {
    tokens.add(String(store.id))
  }
  if (store.name) {
    tokens.add(normalizeFilterToken(store.name))
  }
  if (store.shop_name) {
    tokens.add(normalizeFilterToken(store.shop_name))
  }

  return tokens
}

const getTaxonomyBrandValues = (product: WooCommerceProduct): string[] => {
  const values: string[] = []

  if (!Array.isArray(product.brands)) {
    return values
  }

  for (const brand of product.brands) {
    if (!(brand && typeof brand === "object")) {
      continue
    }

    const record = brand as Record<string, unknown>
    if (typeof record.slug === "string" && record.slug.trim().length > 0) {
      values.push(record.slug)
    }
    if (typeof record.name === "string" && record.name.trim().length > 0) {
      values.push(record.name)
    }
  }

  return values
}

const getAttributeBrandValues = (product: WooCommerceProduct): string[] => {
  const values: string[] = []

  for (const attribute of product.attributes) {
    const isBrandAttribute =
      attribute.slug === "pa_brand" ||
      attribute.slug === "brand" ||
      attribute.name.toLowerCase() === "brand"

    if (!isBrandAttribute) {
      continue
    }

    for (const option of attribute.options) {
      if (option.trim().length > 0) {
        values.push(option)
      }
    }
  }

  return values
}

export const getProductBrandValues = (
  product: WooCommerceProduct
): string[] => [
  ...getTaxonomyBrandValues(product),
  ...getAttributeBrandValues(product),
]

export const matchesBrand = (
  product: WooCommerceProduct,
  selectedBrand: string
): boolean => {
  const normalizedBrand = normalizeFilterToken(selectedBrand)

  for (const brandValue of getProductBrandValues(product)) {
    const token = normalizeFilterToken(brandValue)
    if (token === normalizedBrand) {
      return true
    }
  }

  return false
}

export const buildWooRatingFilter = (minRating: number): string => {
  const clampedMin = Math.min(5, Math.max(1, minRating))
  const ratings: string[] = []

  for (let rating = clampedMin; rating <= 5; rating += 1) {
    ratings.push(String(rating))
  }

  return ratings.join(",")
}

export const isBadRequest = (error: unknown): boolean => {
  const axiosError = error as AxiosError | undefined
  return axiosError?.response?.status === 400
}
